// A linked chart follows its sheet (SD): in the owner's own tab, a moment after anything on the workspace changes, each
// chart on a deck that is linked to a range of a sheet board reads that range again (its sheet's own engine, loaded
// only when a linked chart exists) and, when what it shows has changed, keeps the new values with the chart, once, so
// every place the deck shows (a shared link, a coworker, a printed page, an agent) has them. A sheet, tab or range
// that is gone puts its words on the chart in place of numbers. Nothing runs on a timer: it is the owner's own copy of
// the workspace, worked out in the browser. The server does the same when a sheet's cells change with every tab shut
// (Fixes 8, worker/workspace.ts followCharts), one way for both (followedChart), so the two agree; this tab still
// fills a chart the moment its link is set or changed here.

import type { Entry, ID, Op } from "../../../../shared/types";
import type { SlideBox } from "../../../../shared/slideDesign";
import type { SlideChart } from "../../../../shared/slideChart";
import { useStore, get } from "../../../state/store";
import { commit } from "../../../state/actions";

let timer: ReturnType<typeof setTimeout> | null = null;
/** what each linked chart last read: its sheet's state and its link, so nothing is worked out twice */
const seen = new Map<string, string>();

useStore.subscribe((s, prev) => {
  if (s.entries === prev.entries && s.boards === prev.boards) return;
  if (!s.workspace || s.guest || s.phase !== "ready") return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void follow(), 900);
});

async function follow() {
  timer = null;
  const s = get();
  if (!s.workspace || s.guest) return;
  const linked: { e: Entry; b: SlideBox }[] = [];
  for (const id in s.entries) {
    const e = s.entries[id];
    if (e.t !== "slide" || !Array.isArray(e.data.boxes)) continue;
    for (const b of e.data.boxes as SlideBox[]) if (b?.kind === "chart" && b.chart?.link) linked.push({ e, b });
  }
  if (!linked.length) return;
  const { readLinked, followedChart } = await import("../../../../shared/slideChartSheet");
  const now = get();
  const boards = new Map([...now.boards, ...now.archived].map((b) => [b.id, b]));
  const bySheet = new Map<ID, Entry[]>();
  const sheetOf = (id: ID) => {
    let list = bySheet.get(id);
    if (!list) {
      list = [];
      for (const k in now.entries) if (now.entries[k].boardId === id) list.push(now.entries[k]);
      bySheet.set(id, list);
    }
    return list;
  };
  const changes = new Map<ID, Map<string, SlideChart>>();
  for (const { e, b } of linked) {
    const link = b.chart!.link!;
    const sheet = sheetOf(link.board);
    // the sheet as it stands: its entries' ids and times; the same state never reads twice
    const state = `${boards.has(link.board) ? 1 : 0}|${JSON.stringify(link)}|${sheet.map((x) => `${x.id}:${x.updatedAt}`).join(",")}`;
    const key = `${e.id}|${b.id}`;
    if (seen.get(key) === state) continue;
    seen.set(key, state);
    // the sheet's own time, so a formula with the time in it reads the same in every tab (no write back and forth)
    const at = sheet.reduce((m, x) => Math.max(m, x.updatedAt), 0) || Date.now();
    const next = followedChart(b.chart!, readLinked(boards.get(link.board), sheet, link, at));
    if (!next) continue;
    const m = changes.get(e.id) ?? new Map<string, SlideChart>();
    m.set(b.id, next);
    changes.set(e.id, m);
  }
  if (!changes.size) return;
  const ops: Op[] = [];
  for (const [id, m] of changes) {
    const e = get().entries[id];
    if (!e || !Array.isArray(e.data.boxes)) continue;
    ops.push({ op: "entry.put", entry: { ...e, data: { ...e.data, boxes: (e.data.boxes as SlideBox[]).map((b) => (m.has(b.id) ? { ...b, chart: m.get(b.id) } : b)) } } });
  }
  // the sheet's values kept with the chart: no step of the user's own to undo
  if (ops.length) commit(ops, "Chart follows its sheet", { history: false });
}
